import React from "react";
import Screen from "../uizard/ui/Screen";

export default function Privacy() {
  return (
    <Screen>
      <StyleTag />
      <div className="legal-wrap">
        <header className="legal-header">
          <h1 className="h1">Privacy Policy</h1>
          <p className="lead">Effective Date: September 5, 2025 · Last reviewed: June 13, 2026</p>
          <p className="entity">
            Entity: <b>ArtiBytes</b> (“ArtiBytes,” “we,” “us,” “our”)
          </p>
          <p className="intro">
            ArtiBytes respects your privacy and protects your personal
            information. This Privacy Policy explains what we collect, how we
            use it, and your rights. See also our{" "}
            <a href="/terms">Terms of Service</a> and{" "}
            <a href="/content-policy">Content Policy</a>.
          </p>
        </header>

        <section className="legal-section">
          <h2 className="h2">1) Information We Collect</h2>
          <h3>Account &amp; Identity</h3>
          <ul>
            <li>Pi Network username and user ID</li>
            <li>Display name, profile image (if provided)</li>
          </ul>
          <h3>Transactional &amp; Usage</h3>
          <ul>
            <li>Credit purchases in Pi and payout records</li>
            <li>Generation activity for images and videos (prompts, parameters, timestamps, success/failure)</li>
            <li>Creator Style submissions (metadata, descriptions)</li>
          </ul>
          <h3>Content</h3>
          <ul>
            <li>Images you upload for generation, reference, or style training</li>
            <li>Images and videos you generate using the service</li>
            <li>Feedback, ratings, or reports you submit</li>
          </ul>
          <h3>Device/Technical</h3>
          <ul>
            <li>IP address, device/browser type, OS, language, app version</li>
            <li>Log data and performance metrics (e.g., latency, errors)</li>
          </ul>
          <h3>Sensitive data</h3>
          <p>
            We do not intentionally collect sensitive personal data. If a specific
            feature requires limited additional data (e.g., wallet info for payouts),
            we’ll tell you and process it only for that purpose.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">2) Why We Process Your Data (GDPR Legal Bases)</h2>
          <ul>
            <li>
              <b>Provide the service (contract):</b> enable image and video generation, style
              submission, creator payouts.
            </li>
            <li>
              <b>Improve and secure (legitimate interests):</b> prevent abuse, debug,
              optimize performance, measure usage.
            </li>
            <li>
              <b>Comply with law (legal obligation):</b> tax/financial records, fraud
              prevention, regulatory requests.
            </li>
            <li>
              <b>Consent (where required):</b> optional communications or features you opt into.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">3) How We Use Your Information</h2>
          <ul>
            <li>Operate and improve image generation, video/animation generation, style training/hosting, and payouts.</li>
            <li>Process Pi transactions (credits, purchases, royalties).</li>
            <li>Prevent abuse and enforce our Terms and Content Policy.</li>
            <li>Provide support and notify you about important changes.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">4) Data Storage, Location &amp; Security</h2>
          <p>ArtiBytes processes and stores data across the following infrastructure:</p>
          <ul>
            <li><b>API &amp; application hosting:</b> Google Cloud Run — Europe (europe-west4, Belgium)</li>
            <li><b>Image generation (Imagen):</b> Google Vertex AI — Europe (europe-west4, Belgium)</li>
            <li><b>Video/animation generation (Veo):</b> Google Vertex AI — United States (us-central1, Iowa)</li>
            <li><b>Database:</b> MongoDB Atlas on Google Cloud — Europe (europe-west1, Belgium)</li>
            <li><b>File storage:</b> Google Cloud Storage — Europe (europe-west4, Belgium)</li>
          </ul>
          <p>
            <b>Important:</b> When you use the animation/video generation feature, your source
            image is temporarily transmitted to and processed in Google’s US-based infrastructure
            (us-central1, Iowa) to generate the video. This transfer is covered by appropriate
            safeguards including Standard Contractual Clauses (SCCs) with Google as processor.
          </p>
          <p>
            When data leaves the EEA for any reason, we use lawful transfer mechanisms (SCCs or
            equivalent). We apply technical and organizational security measures including
            encryption in transit and at rest, access controls, least-privilege principles, and
            monitoring. No system is 100% secure.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">5) Retention</h2>
          <ul>
            <li>
              <b>Account &amp; transaction data:</b> retained as long as you have an
              account and as required by law (e.g., 6–10 years for financial records).
            </li>
            <li>
              <b>Generated images, videos &amp; logs:</b> retained for service operation and
              troubleshooting, then pseudonymized or deleted on a schedule.
            </li>
            <li>
              <b>Uploaded assets for training:</b> retained while your Style is active;
              minimal artifacts may be retained post-termination to meet legal, compliance,
              audit, security, or operational obligations (see Creator Agreement).
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="h2">6) Sharing of Information</h2>
          <p>We do not sell or rent your data. We share only as needed:</p>
          <ul>
            <li>Service providers / processors (e.g., Google Cloud for hosting, AI processing, and storage; MongoDB Atlas for database services)</li>
            <li>Payment/wallet integrations (Pi Network ecosystem services as needed)</li>
            <li>Legal/Compliance: courts, regulators, or to protect rights and safety</li>
          </ul>
          <p>All processors are bound by contracts and only process data on our instructions.</p>
        </section>

        <section className="legal-section">
          <h2 className="h2">7) Your Rights (EU/EEA)</h2>
          <p>
            You may have the right to access, rectify, erase, restrict, port, and object to
            processing. You can also withdraw consent at any time (where processing relies
            on consent). Request these rights or data deletion at:{" "}
            <a href="mailto:jaytayeldev@gmail.com">jaytayeldev@gmail.com</a>. You may lodge
            a complaint with your local data protection authority.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">8) AI-Generated Content</h2>
          <p>
            All images and videos generated through ArtiBytes are produced by artificial
            intelligence models. Outputs are created algorithmically based on your prompts and
            inputs. ArtiBytes makes no representations that outputs are indistinguishable from
            human-created content. You are responsible for reviewing outputs before use and
            ensuring your use complies with applicable laws and platform policies regarding
            AI-generated content.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">9) Children</h2>
          <p>
            ArtiBytes is not directed to children under 16. Do not use ArtiBytes if you are
            under the applicable minimum age in your region.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">10) Cookies &amp; Similar Technologies</h2>
          <p>
            We use necessary cookies and similar technologies for authentication, security,
            and performance. Where required, we obtain consent for non-essential cookies.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">11) International Transfers</h2>
          <p>
            Where we transfer personal data outside your country (including to the United States
            for video generation processing), we use lawful transfer mechanisms (e.g., SCCs) and
            require recipients to protect your data to EEA standards.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="h2">12) Contact</h2>
          <p>
            For privacy questions or requests:{" "}
            <a href="mailto:jaytayeldev@gmail.com">jaytayeldev@gmail.com</a>
          </p>
        </section>
      </div>
    </Screen>
  );
}

/* ------------------------------ Styles ------------------------------ */
function StyleTag() {
  return (
    <style>{`
.legal-wrap{
  max-width: 860px;
  margin: 0 auto;
}
.legal-header .entity,
.legal-header .intro{
  color: #6B7280;
}
.legal-header .intro a{
  color: #6D5EF9; text-decoration: none; font-weight: 700;
}
.legal-header .intro a:hover{ text-decoration: underline; }
.legal-section{ margin-top: 18px; }
.legal-section p{ margin: 8px 0; line-height: 1.6; }
.legal-section ul{ margin: 8px 0 0 20px; display: grid; gap: 6px; }
.legal-section h3{ margin: 8px 0 6px; font-size: 16px; }
    `}</style>
  );
}